import React, {FC, ReactElement} from 'react';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import {Link} from "react-router-dom";
import {
    Autocomplete,
    Avatar,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    Grid,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    TextField,
    Typography
} from "@mui/material";
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import "./BaseNavBar.css"
import {Auth} from "aws-amplify";
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import {TreeItem, TreeView} from '@mui/lab';
import * as H from "history";
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {selectedCompanyStore} from "../store/SelectedCompany";
import {ReadCompanyDto} from "../model/company/ReadCompanyDto";
import {roleIcon, UserRole} from "../model/user/UserRole";
import {userApi} from "../backend/UserApi";
import {CompaniesStoreStates, userInfoStore} from "../store/UserInfo";
import {ScreenType, WithScreenType} from "../components/ScreenType";
import {observer} from "mobx-react";
import PopupState, {bindHover, bindMenu} from 'material-ui-popup-state'
import MenuHover from 'material-ui-popup-state/HoverMenu'
import {logEvent} from "../utils/SentryUtils";
import {NavigateFunction} from "react-router/lib/hooks";
import {LOCALE_EN, LOCALE_ET, LOCALE_RU} from "../locale/i18";
import {uiStateStore} from "../store/UiState";
import {TFunction, Trans} from "react-i18next";

export interface NavigationItemSingle {
    label: string
    path: string
    inNewTab?: boolean
}

export interface NavigationItemMenu extends NavigationItemSingle {
    items?: NavigationItemSingle[]
}

type NavItemInternal = NavigationItemMenu;

interface IProps {
    navigate: NavigateFunction;
    location: H.Location,
    userMenuItems: ReactElement[]
    navigationItems: NavItemInternal[]
}

interface IState {
    loading: boolean,
    email: string,
    currentCompanyMenuAnchorEl: null | HTMLElement;
    navMenuOpen: boolean;
}

const NewWindowLink: FC<any> = React.forwardRef((props, _) => {
    return <a {...props}>{props.children}</a>
});

export const translateNavItems = (items: NavigationItemMenu[], t: TFunction) => {
    const translateLabel = (label: string) => t(`nav_bar.${label}`, label)
    return items.map(topLevel => {
        const translatedItems = topLevel.items ? topLevel.items.map(it => ({...it, label: translateLabel(it.label)})) : undefined
        return {
            ...topLevel,
            label: translateLabel(topLevel.label),
            items: translatedItems
        }
    });
}

const truncateEmailByScreenType = (email: string, type: ScreenType) => {
    if (type <= ScreenType.MD) {
        return truncateEmail(email, 13)
    } else if (type <= ScreenType.LG) {
        return truncateEmail(email, 18)
    } else {
        return truncateEmail(email, 34)
    }
}

const truncateEmail = (email: string, maxLength: number) => {
    if (email.length <= maxLength) {
        return email;
    }

    const atIndex = email.lastIndexOf('@');
    const username = email.slice(0, atIndex);
    const domain = email.slice(atIndex);

    const domainParts = domain.split('.');
    const tld = domainParts.pop()!!; // Get the top-level domain
    const domainWithoutTLD = domainParts.join('.'); // Domain without TLD

    let truncatedUsername = username;
    let truncatedDomain = domainWithoutTLD;

    while (truncatedUsername.length + truncatedDomain.length + tld.length > maxLength - 3) {
        const usernameExceeded = truncatedUsername.length > Math.floor((maxLength - tld.length - 3) / 2);
        const domainExceeded = truncatedDomain.length > Math.floor((maxLength - tld.length - 3) / 2);

        if (usernameExceeded && domainExceeded) {
            if (truncatedUsername.length > truncatedDomain.length) {
                truncatedUsername = truncatedUsername.slice(0, -1);
            } else {
                truncatedDomain = truncatedDomain.slice(0, -1);
            }
        } else if (usernameExceeded) {
            truncatedUsername = truncatedUsername.slice(0, -1);
        } else {
            truncatedDomain = truncatedDomain.slice(0, -1);
        }
    }

    const truncatedDomainWithEllipsis = truncatedDomain.length < domainWithoutTLD.length ? truncatedDomain + '...' : truncatedDomain + '.';

    return truncatedUsername + truncatedDomainWithEllipsis + tld;
}

@observer
export default class BaseNavBar extends React.Component<IProps, IState> {

    private navProperties(item: NavigationItemSingle, selectedCompany: ReadCompanyDto | null) {
        if (item.inNewTab) {
            return {
                value: item.path,
                href: item.path,
                disabled: selectedCompany == null,
                component: NewWindowLink,
                target: "_blank",
                rel: "noopener noreferrer"
            };
        } else {
            return {
                value: item.path,
                to: item.path,
                disabled: selectedCompany == null,
                component: Link,
            };
        }
    }

    constructor(props: IProps) {
        super(props);
        this.state = {
            email: "",
            loading: true,
            currentCompanyMenuAnchorEl: null,
            navMenuOpen: false
        };
    }


    async componentDidMount() {
        this.setState({
            email: userInfoStore.email,
            loading: false,
        });
    }

    private calcCompanyProperties() {
        const selectedCompanyId = selectedCompanyStore.companyId;
        return userInfoStore.findById(selectedCompanyId);
    }


    // @ts-ignore
    render() {
        const path = this.props.location.pathname;
        const tabCanBeSelected = this.props.navigationItems.find(it => it.path === path || it.items?.find(sub => sub.path === path));
        const selectedTab = tabCanBeSelected ? tabCanBeSelected.path : false

        const {email, loading, navMenuOpen} = this.state;
        const selectedCompany = this.calcCompanyProperties();
        const companies = userInfoStore.companies
                .map(this.convertToOptionForSelect)
                .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));

        const loadingCompaniesData = userInfoStore.companiesLoadingState !== CompaniesStoreStates.DONE;
        return (
                <AppBar position="static" color={"default"} className="no-print">
                    <Grid container>
                        <WithScreenType render={(type) => {
                            if (type > ScreenType.SM) {
                                return <Grid item md={7} lg={8}>
                                    <Tabs
                                            value={selectedTab}
                                            indicatorColor="primary"
                                            textColor="primary"
                                            variant="scrollable"
                                            scrollButtons="auto">
                                        {
                                            this.props.navigationItems.map(it => this.renderTab(it, selectedCompany, path))
                                        }
                                    </Tabs>
                                </Grid>
                            } else {
                                return (
                                        <Grid container item xs={2} justifyContent="center" alignItems="center">
                                            <IconButton
                                                    color="inherit"
                                                    edge="start"
                                                    onClick={this.handleNavMenuClick}
                                                    size="large">
                                                <MenuIcon/>
                                            </IconButton>
                                            <Drawer anchor="left" open={navMenuOpen} onClose={this.handleNavMenuClose}>
                                                <List>
                                                    {
                                                        this.props.navigationItems.flatMap(it => this.renderMobileNav("", it, selectedCompany, path))
                                                    }
                                                </List>
                                            </Drawer>
                                        </Grid>
                                );
                            }
                        }}/>

                        <Grid container item xs={10} md={5} lg={4} justifyContent="flex-end" alignItems="center">
                            <Autocomplete
                                    id="select-company"
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    value={selectedCompany ? this.convertToOptionForSelect(selectedCompany) : null}
                                    options={companies}
                                    groupBy={(option) => option.firstLetter}
                                    getOptionLabel={(option) => option.name}
                                    style={{width: "15rem"}}
                                    renderInput={(params) =>
                                            <TextField
                                                    {...params}
                                                    label={<Trans i18nKey="nav_bar.company_selector">Select a company</Trans>}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                                <React.Fragment>
                                                                    {loadingCompaniesData ? <CircularProgress color="inherit" size={20}/> : null}
                                                                    {params.InputProps.endAdornment}
                                                                </React.Fragment>
                                                        ),
                                                    }}
                                            />
                                    }
                                    onChange={this.handleSelectedCompanyChanged}
                                    disabled={loadingCompaniesData}
                            />
                            <WithScreenType render={(type) => {
                                if (type > ScreenType.SM) {
                                    return <Chip className="user-info-container"
                                                 variant="outlined"
                                                 avatar={<Avatar><AccountCircleOutlinedIcon/></Avatar>}
                                                 label={(
                                                         loading ?
                                                                 <CircularProgress/> :
                                                                 <section>
                                                                     <div className="user-email">{truncateEmailByScreenType(email, type)}</div>
                                                                 </section>
                                                 )}
                                                 clickable={true}
                                                 onClick={this.handleCurrentCompanyMenuClick}
                                    />
                                } else {
                                    return <Avatar onClick={this.handleCurrentCompanyMenuClick}><AccountCircleOutlinedIcon/></Avatar>
                                }
                            }}/>

                            <Menu
                                    id="menu"
                                    anchorEl={this.state.currentCompanyMenuAnchorEl}
                                    keepMounted
                                    open={Boolean(this.state.currentCompanyMenuAnchorEl)}
                                    onClose={this.handleCurrentCompanyMenuClose}
                            >
                                <MenuItem disabled={true} onClick={this.handleCurrentCompanyMenuClose}>
                                    <ListItemIcon>
                                        <BusinessCenterOutlinedIcon/>
                                    </ListItemIcon>
                                    <ListItemText primary={selectedCompany ? selectedCompany.name : '<No company>'}/>
                                </MenuItem>
                                {this.props.userMenuItems.map((it, index) => {
                                    return {
                                        ...it, props: {
                                            ...it.props, onClick: () => {
                                                if (it.props.onClick) {
                                                    it.props.onClick()
                                                }
                                                this.handleCurrentCompanyMenuClose()
                                            }
                                        },
                                        key: index,
                                    }
                                })}
                                <Divider/>
                                <LanguageSelector/>
                                {userInfoStore.canSelectRoles() &&
                                        [
                                            <Divider key="act-as-selector-divider"/>,
                                            this.renderRoleSelector()
                                        ]
                                }
                                <Divider/>
                                <MenuItem onClick={this.logOut}>
                                    <Typography variant="body2"> <Trans i18nKey="user_menu.Logout">Logout</Trans></Typography>
                                </MenuItem>
                            </Menu>
                        </Grid>
                    </Grid>
                </AppBar>
        );
    }

    private renderMobileNav(prefix: string, it: NavigationItemMenu, selectedCompany: ReadCompanyDto | null, path: string): ReactElement[] {
        if (it.items) {
            return it.items.flatMap(sub => this.renderMobileNav(`${prefix}${it.label} -> `, sub, selectedCompany, path))
        } else {
            const label = prefix + it.label;
            return [
                <ListItemButton
                        key={label}  {...this.navProperties(it, selectedCompany)}
                        onClick={this.handleNavMenuClose}
                        disabled={selectedCompany == null || it.path === path}
                >
                    <ListItemText primary={label}/>
                </ListItemButton>
            ];
        }
    }

    private renderTab = (navItem: NavigationItemMenu, selectedCompany: ReadCompanyDto | null, path: string) => {
        if (navItem.items) {
            return <PopupState variant="popover" popupId={navItem.label} disableAutoFocus
                               key={navItem.label} {...this.navProperties(navItem, selectedCompany)}>
                {(popupState) => {
                    const navItemLen = navItem.label.length * 0.8;
                    return (
                            <>
                                <Tab key={navItem.label}
                                     label={navItem.label} {...this.navProperties(navItem, selectedCompany)}
                                     {...bindHover(popupState)}
                                     onClick={popupState.close}
                                     style={{width: `${navItemLen}rem`, minWidth: `${navItemLen}rem`}}
                                />
                                <MenuHover
                                        {...bindMenu(popupState)}
                                        PaperProps={{
                                            style: {minWidth: popupState.anchorEl ? popupState.anchorEl.offsetWidth : undefined},
                                        }}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        }}
                                >
                                    {navItem.items?.map(subItem => <MenuItem
                                                    key={subItem.label} {...this.navProperties(subItem, selectedCompany)}
                                                    onClick={popupState.close}
                                                    disabled={path === subItem.path}
                                            >{subItem.label}</MenuItem>
                                    )}
                                </MenuHover>
                            </>
                    );
                }}
            </PopupState>;
        } else {
            return <Tab key={navItem.label} label={navItem.label} {...this.navProperties(navItem, selectedCompany)}/>
        }
    }

    private selectNewRole = (role: UserRole) => async () => {
        await userApi.selectNewRole(role);
        logEvent(`user`, `New role selected ${role}`);
        window.location.href = "/";
    }

    private renderRoleSelector() {
        const currentRole = userInfoStore.userInfo.currentRole;
        const actingOption = (label: string, role: UserRole) => {
            return <TreeItem nodeId={role} label={
                <ListItemButton component="div" disabled={currentRole === role} onClick={this.selectNewRole(role)}>
                    <ListItemIcon>
                        {roleIcon(role)}
                    </ListItemIcon>
                    <ListItemText primary={label}/>
                </ListItemButton>
            }/>
        }

        return <MenuItem key="act-as-selector">
            <TreeView defaultCollapseIcon={<ExpandMoreIcon/>}
                      defaultExpandIcon={<ChevronRightIcon/>}
                      disableSelection
                      style={{width: "100%"}}>
                <TreeItem nodeId="1" label={<ListItemText primary="Acting as"/>}>
                    {actingOption("Accountant", UserRole.ACCOUNTANT)}
                    {actingOption("Client", UserRole.CLIENT)}
                </TreeItem>
            </TreeView>
        </MenuItem>
    }


    private convertToOptionForSelect = (option: ReadCompanyDto) => {
        const firstLetter = option.name[0].toUpperCase();
        return {
            firstLetter: /[0-9]/.test(firstLetter) ? '0-9' : firstLetter,
            ...option,
        };
    };

    private logOut = async () => {
        this.handleCurrentCompanyMenuClose();
        await Auth.signOut();
    }

    private handleCurrentCompanyMenuClick = (event: any) => {
        this.setState({currentCompanyMenuAnchorEl: event.currentTarget})
    };

    private handleCurrentCompanyMenuClose = () => {
        this.setState({currentCompanyMenuAnchorEl: null})
    };

    private handleNavMenuClick = () => {
        this.setState({navMenuOpen: true})
    };

    private handleNavMenuClose = () => {
        this.setState({navMenuOpen: false})
    };


    private handleSelectedCompanyChanged = (event: any, value: ReadCompanyDto | null) => {
        if (value) {
            selectedCompanyStore.companyId = value.id;
        } else {
            selectedCompanyStore.companyId = '';
        }

        this.navigateUpperAndRefreshPage();
    }

    private navigateUpperAndRefreshPage = () => {
        this.props.navigate("/");
    }
}

const LanguageSelector = () => {
    const LangOption = observer(({label, locale}: { label: string, locale: string }) => {
        const language = uiStateStore.language;
        return <TreeItem nodeId={locale} label={
            <ListItemButton component="div" disabled={locale === language} onClick={() => {
                uiStateStore.language = locale
                logEvent(`user`, `Language selected ${locale}`)
            }}>{label}</ListItemButton>
        }/>
    })

    return <MenuItem>
        <TreeView defaultCollapseIcon={<ExpandMoreIcon/>}
                  defaultExpandIcon={<ChevronRightIcon/>}
                  disableSelection
                  style={{width: "100%"}}>
            <TreeItem nodeId="1" label={<ListItemText primary={<Trans i18nKey="user_menu.Language">Language</Trans>}/>}>
                <LangOption label="EN" locale={LOCALE_EN}/>
                <LangOption label="ET" locale={LOCALE_ET}/>
                <LangOption label="RU" locale={LOCALE_RU}/>
            </TreeItem>
        </TreeView>
    </MenuItem>
}
